import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useUpdateEndpointTestSuite } from '@/hooks/useEndpointTestEvals';
import { useToast } from '@/contexts/ToastContext';
import { SourceBadge } from './SourceBadge';
import type { EndpointTest, EndpointTestSuite } from '@/types/endpointTests';

interface SuiteMembershipDialogProps {
  endpointId: string;
  suite: EndpointTestSuite;
  /** Every test for the endpoint, in list order. */
  tests: EndpointTest[];
  onClose: () => void;
}

/**
 * Edit a suite's tests (task 1209): every test for the endpoint with a
 * checkbox, members pre-checked. Save PATCHes `testIds`, keeping the saved
 * order for members that stay and appending new ones in list order. Save is
 * disabled with nothing checked — the service rejects an empty list.
 */
export function SuiteMembershipDialog({ endpointId, suite, tests, onClose }: SuiteMembershipDialogProps) {
  const [checked, setChecked] = useState<Set<string>>(() => new Set(suite.testIds));
  const updateSuite = useUpdateEndpointTestSuite(endpointId);
  const toast = useToast();

  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSave = async () => {
    const kept = suite.testIds.filter((id) => checked.has(id));
    const added = tests.map((test) => test.id).filter((id) => checked.has(id) && !kept.includes(id));
    try {
      await updateSuite.mutateAsync({ id: suite.id, data: { testIds: [...kept, ...added] } });
      toast.success('Suite updated', suite.name);
      onClose();
    } catch (err) {
      toast.error('Failed to update suite', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="suite-membership-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <h3 id="suite-membership-title" className="text-lg font-medium text-gray-900 dark:text-white">
            {`Edit scenarios · ${suite.name}`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <ul data-testid="suite-membership-list" className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
          {tests.map((test) => (
            <li key={test.id} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
              <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <input
                  type="checkbox"
                  aria-label={test.name}
                  checked={checked.has(test.id)}
                  onChange={() => toggle(test.id)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="truncate">{test.name}</span>
                <SourceBadge source={test.source} />
              </label>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {checked.size} {checked.size === 1 ? 'scenario' : 'scenarios'} in this suite
          </span>
          <span className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={updateSuite.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={checked.size === 0 || updateSuite.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

export default SuiteMembershipDialog;
